import React, { useState, useEffect } from 'react';
import ApproverDashboardTable from '../../components/ApproverDashboardTable/ApproverDashboardTable';
import styles from './ApproverDashboard.module.css';
import EditNoteIcon from '@mui/icons-material/EditNote';
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';
import DashboardConfiguration from '../../components/DashboardConfiguration/DashboardConfiguration';
import RadioGroupWithDelete from '../../components/RadioGroupwithDelete/RadioGroupwithDelete';
import EditableField from '../../components/EditableField/EditableField';
import ApproveButton from '../../components/ApproveButton/ApproveButton';
import Modal from 'react-modal';
import useDashboardQuery from '../../hooks/useDashboardQuery';
import useProfileQuery from '../../hooks/useProfileQuery';
import useApproval from '../../hooks/useApproval';
import keycloak from '../../keycloak';

const columns = [
    'date',
    'invoiceID',
    'supplierEmail',
    'expenseType',
    'category',
    'originalAmount',
    'status',
    'paidDate',
    'GST',
    'PST',
    'distance',
    'project',
    'entity',
    'allocation'
];

const columnsMap = {
    'date': 'Date',
    'invoiceID': '#',
    'supplierEmail': 'Submitted By',
    'expenseType': 'Expense Type',
    'category': 'Category',
    'originalAmount': 'Sub Total ($)',
    'status': 'Status',
    'paidDate': 'Remittance Date',
    'GST': 'GST ($)',
    'PST': 'PST ($)',
    'distance': 'Distance',
    'project': 'Project',
    'entity': 'Entity',
    'allocation': 'Allocation'
};

export default function ApproverDashboard() {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isApprovalModalOpen, setIsApprovalModalOpen] = useState(false);
    const [selectedColumns, setSelectedColumns] = useState(columns);
    const [draftSelectedColumns, setDraftSelectedColumns] = useState(columns);
    const [rowSelection, setRowSelection] = useState({});
    const [data, setData] = useState([]);

    //clicking on a row opens a page with the invoice details 
    const [selectedConfigOption, setSelectedConfigOption] = useState('List All');
    const [draftSelectedConfigOption, setDraftSelectedConfigOption] = useState('List All');
    const [configName, setConfigName] = useState('');

    const [isPendingDataShown, setisPendingDataShown] = useState(true);
    const [isApprovalDataShown, setisApprovalDataShown] = useState(false);
    const [isRejectedDataShown, setisRejectedDataShown] = useState(false);
    const [isEditReqDataShown, setisEditReqDataShown] = useState(false);
    const [activeTab, setActiveTab] = useState('pending');
    const [showColumnFilters, setShowColumnFilters] = useState(false);
    const [showSearchInput, setShowSearchInput] = useState(false);
    const [globalFilter, setGlobalFilter] = useState('');
    const searchInputRef = React.useRef(null);

    const applyActiveTab = (tab) => {
        const normalizedTab = ['pending', 'approved', 'rejected', 'editRequired'].includes(tab) ? tab : 'pending';
        setisPendingDataShown(normalizedTab === 'pending');
        setisApprovalDataShown(normalizedTab === 'approved');
        setisRejectedDataShown(normalizedTab === 'rejected');
        setisEditReqDataShown(normalizedTab === 'editRequired');
        setActiveTab(normalizedTab);
        sessionStorage.setItem('approverActiveTab', normalizedTab);
    };

    const {loading, data: dashboardData, refetch: dashboardRefetch} = useDashboardQuery();
    const {updateMyProfile} = useProfileQuery();
    const email = keycloak.tokenParsed?.email; ;

    const [configOptions, setConfigOptions] = useState([]);
    const [configColumnMapping, setConfigColumnMapping] = useState({'List All': columns});

    const handleSelectConfigOption = (option) => {
        setDraftSelectedConfigOption(option);
        setDraftSelectedColumns(configColumnMapping[option] || []);
    };

    const handleCheckboxChange = (value) => {
        if (draftSelectedColumns.includes(value)) {
            setDraftSelectedColumns(draftSelectedColumns.filter((checkbox) => checkbox !== value));
        } else {
            setDraftSelectedColumns([...draftSelectedColumns, value]);
        }
    };

    const customModalStyles = {
        content: {
            top: '50%',
            left: '50%',
            right: 'auto',
            bottom: 'auto',
            marginRight: '-50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: 'var(--theme-bg-elevated)',
        },
        overlay: {
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
        },
    };

    const openModal = () => {
        setDraftSelectedColumns(selectedColumns);
        setDraftSelectedConfigOption(selectedConfigOption);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
    };

    const closeApprovalModal = () => {
        setIsApprovalModalOpen(false);
        setRowSelection({});
        window.location.reload();
    };

    useEffect(() => {
        if (dashboardData && dashboardData.getUserProfile && dashboardData.getUserProfile.body) {
            const userConfig = dashboardData.getUserProfile.body;
            
            if (userConfig.dashboardCfg !== undefined && userConfig.dashboardCfg !== null && userConfig.dashboardCfg !== 'NULL') {
                if (userConfig.dashboardCfg !== "InvoiceID"){
                    let parsedDashCfg = JSON.parse(userConfig.dashboardCfg);
                    setConfigColumnMapping(parsedDashCfg);

                    setConfigOptions(Object.keys(parsedDashCfg));
                }
            }
        }
    }, [dashboardData]);

    useEffect(() => {
        const savedTab = sessionStorage.getItem('approverActiveTab');
        if (savedTab) {
            applyActiveTab(savedTab);
        }
    }, []);

    const handleSave = async() => {
        const typedName = configName.trim();
        // No new name typed while an existing (non-default) config is selected: update it in place.
        const isEditingExisting = !typedName && draftSelectedConfigOption && draftSelectedConfigOption !== 'List All';
        const targetName = isEditingExisting ? draftSelectedConfigOption : typedName;

        if (!targetName) {
            alert('Please enter a configuration name before saving.');
            return;
        }
        const updatedConfigOptions = configOptions.includes(targetName)
            ? configOptions
            : [...configOptions, targetName];
        const updatedMapping = { ...configColumnMapping, [targetName]: draftSelectedColumns };

        setConfigOptions(updatedConfigOptions);
        setConfigColumnMapping(updatedMapping);
        setDraftSelectedConfigOption(targetName);
        setConfigName('');

        let changes = {"dashboardCfg": JSON.stringify(updatedMapping)};
        try {
            await updateMyProfile(email, changes);
            dashboardRefetch();
        } catch (error) {
            console.error("Error updating dashboard configuration:", error);
        }

    };

    const handleApply = () => {
        setSelectedColumns(draftSelectedColumns);
        setSelectedConfigOption(draftSelectedConfigOption);
        closeModal();
        setConfigName('');
    };

    const handleconfigChange = (value) => {
        setConfigName(value || '');
    };

    const deleteConfigOption = async(optionToDelete) => {
        const updatedConfigOptions = configOptions.filter(option => option !== optionToDelete);
        const updatedMapping = {};

        Object.keys(configColumnMapping).forEach(option => {
            if (option !== optionToDelete) {
                updatedMapping[option] = configColumnMapping[option];
            }
        });

        setConfigOptions(updatedConfigOptions);
        setConfigColumnMapping(updatedMapping);

        let changes = {"dashboardCfg": JSON.stringify(updatedMapping)};
        try {
            await updateMyProfile(email, changes);
            refetch();
        } catch (error) {
            console.error("Error updating dashboard configuration:", error);
        }
    };

    const {approveBatchInvoices, refetch} = useApproval();
    
    const handleApprove = async() => {
        let isAnyCheckboxSelected = Object.values(rowSelection).some(selected => selected);

        if (isAnyCheckboxSelected && isPendingDataShown) {
            const selectedRows = Object.keys(rowSelection);

            // Filter out the selected rows and extract the invoiceIDs
            const selectedInvoiceIDs = selectedRows.filter(row => rowSelection[row]).map(([index]) => data[index].invoiceID);

            try {
                await approveBatchInvoices(selectedInvoiceIDs, setIsApprovalModalOpen);
            } catch (error) {
                console.error("Error approving invoices by batch:", error);
            } 
        } else if (!isAnyCheckboxSelected) {
            alert("No invoice selected.")
        } else {
            alert("Only pending invoices can be approved.")
        }
    };

    const handlePendingToggle = () => {
        applyActiveTab('pending');
    };

    const handleApprovedToggle = () => {
        applyActiveTab('approved');
        
    };

    const handleRejectedToggle = () => {
        applyActiveTab('rejected');
    };

    const handleEditReqToggle = () => {
        applyActiveTab('editRequired');
    };

    return (
        <div className={styles.pageContainer}>
            <div className={styles.header}>
                <div className={styles.buttonContainer}>
                    <div style={{ padding: "0px" }}> <ApproveButton handleApprove={handleApprove} /> </div>
                </div>

                <div className={styles.statusContainer}>
                    <div className={styles.statusRow}>
                        <button onClick={handlePendingToggle} className={`${styles.statusButton} ${isPendingDataShown ? styles.selected : ''}`}>
                            Pending
                        </button>
                    </div>

                    <div className={styles.statusRow}>
                        <button onClick={handleApprovedToggle} className={`${styles.statusButton} ${isApprovalDataShown ? styles.selected : ''}`}>
                            Approved
                        </button>
                    </div>

                    <div className={styles.statusRow}>
                        <button onClick={handleRejectedToggle} className={`${styles.statusButton} ${isRejectedDataShown ? styles.selected : ''}`}>
                            Rejected
                        </button>
                    </div>

                    <div className={styles.statusRow}>
                        <button onClick={handleEditReqToggle} className={`${styles.statusButton} ${isEditReqDataShown ? styles.selected : ''}`}>
                            Edit Requested
                        </button>
                    </div>
                </div>
                <div className={styles.controlsContainer}>
                    <button
                        className={styles.externalControlButton}
                        onClick={() => setShowColumnFilters((prev) => !prev)}
                    >
                        <TuneIcon fontSize="small" />
                        {showColumnFilters ? 'Hide Filters' : 'Show Filters'}
                    </button>
                    {showSearchInput ? (
                        <input
                            ref={searchInputRef}
                            className={styles.searchInlineInput}
                            type="text"
                            placeholder="Search invoices..."
                            value={globalFilter || ''}
                            onChange={(e) => setGlobalFilter(e.target.value)}
                            onBlur={() => {
                                if (!globalFilter) setShowSearchInput(false);
                            }}
                        />
                    ) : (
                        <button
                            className={styles.externalControlButton}
                            onClick={() => {
                                setShowSearchInput(true);
                                setTimeout(() => searchInputRef.current?.focus(), 0);
                            }}
                        >
                            <SearchIcon fontSize="small" />
                            Search
                        </button>
                    )}
                </div>
                <div className={styles.iconContainer}>
                    <EditNoteIcon className={styles.editIcon} fontSize='large' onClick={openModal} />
                </div>
            </div>
            
            <ApproverDashboardTable
                selectedColumns={selectedColumns}
                rowSelection={rowSelection}
                setRowSelection={setRowSelection}
                activeTab={activeTab}
                data={data}
                setData={setData}
                showColumnFilters={showColumnFilters}
                globalFilter={globalFilter}
                onGlobalFilterChange={setGlobalFilter}
            />
            {isModalOpen && (
                <div className={styles.modalContainer}>
                    <DashboardConfiguration title="Dashboard Configuration" onClose={closeModal}>
                        <div>
                            Select existing configuration:
                            <div className={styles.selectConfig}>
                                <RadioGroupWithDelete
                                    options={configOptions}
                                    selectedOption={draftSelectedConfigOption}
                                    onChange={handleSelectConfigOption}
                                    handleDeletion={(option) => deleteConfigOption(option)} />
                            </div>
                        </div>

                        <div className={styles.configName}>
                            Configuration Name:
                            <div style={{ width: "200px" }}>
                                <EditableField isEditing={true} onChange={handleconfigChange} initialData={configName} />
                            </div>
                        </div>

                        <div className={styles.modalContent}>
                            <div className={styles.configCheckbox}>
                                {columns.map((value, index) => (
                                    <div key={index} className={styles.checkboxItem}>
                                            <input type="checkbox"
                                            id={`checkbox${index}`}
                                            value={value}
                                            checked={draftSelectedColumns.includes(value)}
                                            onChange={() => handleCheckboxChange(value)} />
                                        <label htmlFor={`checkbox${index}`}>{columnsMap[value]}</label>
                                    </div>
                                ))}
                            </div>
                            <div className={styles.buttonGroup}>
                                <button className={styles.saveButton} onClick={handleSave}>
                                    Save
                                </button>
                                <button className={styles.applyButton} onClick={handleApply}>
                                    Apply
                                </button>
                            </div>
                        </div>
                    </DashboardConfiguration>
                </div>
            )}

            <Modal
                isOpen={isApprovalModalOpen}
                onRequestClose={closeApprovalModal}
                style={customModalStyles}
                contentLabel="Submission Result"
            >
                <button className={styles.closeButton} onClick={closeApprovalModal}>
                    <span className={styles.closeIcon}>x</span>
                </button>
                <h2 className={styles.successMessage}>You have successfully approved {Object.keys(rowSelection).length} invoice(s).</h2>
            </Modal>
        </div>
    )
}
